import React, { useMemo } from 'react';
import classnames from 'classnames';
import GameDefault from '@/pages/home/assets/img/common/game-default.svg';
import DragHandle from '@sg/assests/svg/draghandle-icon.svg';
import LiveMetricBoard from '@sg/pages/StudioManagement/LiveTitles/pages/LiveVisualizationAll/component/LiveTitlesOverview/component/LiveMetricBoard';
import {
  formatMoM,
  formatShortMoney,
  formatPercent,
} from '@sg/pages/StudioManagement/LiveTitles/pages/LiveVisualizationAll/component/LiveTitlesOverview/component/ProjectRow/utils';
import GRDecisionCell from '../GRDecisionCell';
import MilestoneCell from '../MilestoneCell';
import {
  getStatusMeta,
  buildTrendData,
  getMetricNumericValue,
} from './utils';
import './index.scss';

interface IProjectRowProps {
  project: any;
  isEditing?: boolean;
  dragHandleProps?: any;
  className?: string;
  onIconClick?: (project: any) => void;
  selectedMonth?: any;
}

const CLS = 'dbs-idp-project-row';

const MetricCellNew: React.FC<{
  metricName: string;
  title: string;
  metric: any;
  numValue: number | null;
  formatter?: string;
  unit?: string;
  chartHeight?: number;
  extraLines?: React.ReactNode;
  withDivider?: boolean;
}> = ({
  metricName,
  title,
  metric,
  numValue,
  formatter,
  unit,
  chartHeight,
  extraLines,
  withDivider = false,
}) => {
  const textList = useMemo(
    () => [{ value: 'MoM', text: formatMoM(metric?.mom) }],
    [metric?.mom],
  );

  const trendData = useMemo(
    () => buildTrendData(metric?.timeline, metricName),
    [metricName, metric?.timeline],
  );

  return (
    <div
      className={classnames(
        `${CLS}__metric`,
        withDivider && `${CLS}__metric--with-divider`,
      )}
    >
      <LiveMetricBoard
        title={title}
        num={numValue}
        formatter={formatter}
        unit={unit}
        chartHeight={chartHeight}
        textList={textList}
        trendData={trendData}
        extraLines={extraLines}
      />
    </div>
  );
};

const ProjectRow: React.FC<IProjectRowProps> = (props) => {
  const {
    project,
    isEditing = false,
    dragHandleProps,
    className,
    onIconClick,
    selectedMonth,
  } = props;

  const statusMeta = getStatusMeta(project?.status);

  const content = (
    <div className={classnames(CLS, className)}>
      <div
        className={classnames(`${CLS}__identity`, `${CLS}__identity--with-divider`)}
        onClick={() => onIconClick?.(project)}
      >
        {statusMeta && (
          <span
            className={classnames(
              `${CLS}__status`,
              `${CLS}__status--${statusMeta.modifier}`,
            )}
          >
            {statusMeta.label}
          </span>
        )}
        <img className={`${CLS}__icon`} src={project?.cover || GameDefault} alt={project?.game_title} />
        <div className={`${CLS}__name`}>{project?.game_title}</div>
      </div>

      <MetricCellNew
        metricName="spend_to_date"
        title="Spend to Date ($)"
        metric={project?.spend_to_date}
        numValue={getMetricNumericValue('spend_to_date', project)}
        formatter="db_bmk0"
        chartHeight={116}
        withDivider
        extraLines={
          <div className={`${CLS}__ytm`}>
            <span className={`${CLS}__ytm-label`}>Approved Total Dev Cost</span>
            <div className={`${CLS}__ytm-value-row`}>
              <strong className={`${CLS}__ytm-value`}>
                {formatShortMoney(project?.spend_to_date?.approved_total_dev_cost)}
              </strong>
              <span className={`${CLS}__ytm-completion`}>
                (Completion: {formatPercent(project?.spend_to_date?.completion_pct)})
              </span>
            </div>
          </div>
        }
      />
      <MetricCellNew
        metricName="headcount"
        title="Headcount"
        metric={project?.headcount}
        numValue={getMetricNumericValue('headcount', project)}
        formatter="db_bmk0"
        chartHeight={116}
        withDivider
      />
      <MilestoneCell
        milestone={project?.current_milestone}
        withDivider
      />
      <GRDecisionCell
        grDecision={project?.gr_decision || project}
        projectName={project?.game_title}
        projectCover={project?.cover}
        milestoneName={project?.current_milestone?.name}
      />
    </div>
  );

  return (
    <div className={`${CLS}-drag-handle-wrapper`}>
      {isEditing && (
        <div className={`${CLS}-drag-handle`} {...dragHandleProps}>
          <img className={`${CLS}-drag-handle__icon`} src={DragHandle} alt="Drag handle" />
        </div>
      )}
      {content}
    </div>
  );
};

export default ProjectRow;
